// File Name: types/jobapplication.js

/**
 * Job Application Type Definition
 * @typedef {Object} JobApplication
 * @property {string} id - Unique identifier for the application (auto-generated by Firestore)
 * @property {string} firstName - Applicant's first name
 * @property {string} lastName - Applicant's last name
 * @property {string} position - Position applied for
 * @property {string} resumeUrl - URL to the uploaded resume PDF from Cloudinary
 * @property {string} resumeFileName - Original filename of the resume
 * @property {string} resumePublicId - Cloudinary public_id for file management
 * @property {string} expertise - Summary of technical expertise
 * @property {string} email - Applicant's email (from auth)
 * @property {string} userId - User ID from Firebase Auth
 * @property {firebase.firestore.Timestamp} appliedAt - Application submission timestamp
 * @property {string} status - Application status (pending, reviewed, rejected, accepted)
 * @property {string} [notes] - Optional notes from HR
 * @property {firebase.firestore.Timestamp} [updatedAt] - Last update timestamp
 */

/**
 * Application status constants
 */
export const APPLICATION_STATUS = {
  PENDING: 'pending',
  REVIEWED: 'reviewed',
  REJECTED: 'rejected',
  ACCEPTED: 'accepted'
};

/**
 * Creates a new job application object
 * @param {Object} params - Application parameters
 * @param {string} params.firstName - Applicant's first name
 * @param {string} params.lastName - Applicant's last name
 * @param {string} params.position - Position applied for
 * @param {string} params.resumeUrl - URL to the uploaded resume from Cloudinary
 * @param {string} params.resumeFileName - Original filename of the resume
 * @param {string} params.resumePublicId - Cloudinary public_id for file management
 * @param {string} params.expertise - Summary of technical expertise
 * @param {string} params.email - Applicant's email
 * @param {string} params.userId - User ID from Firebase Auth
 * @returns {Omit<JobApplication, 'id' | 'appliedAt' | 'status' | 'notes' | 'updatedAt'>} - New application object
 */
export const createJobApplication = ({
  firstName,
  lastName,
  position,
  resumeUrl,
  resumeFileName,
  resumePublicId,
  expertise,
  email,
  userId
}) => ({
  firstName,
  lastName,
  position,
  resumeUrl,
  resumeFileName,
  resumePublicId,
  expertise,
  email,
  userId,
  appliedAt: new Date(), // Will be converted to Firestore Timestamp
  status: APPLICATION_STATUS.PENDING
});

/**
 * Validates a job application object
 * @param {Object} application - Application object to validate
 * @returns {Object} - Validation result { isValid: boolean, errors: string[] }
 */
export const validateJobApplication = (application) => {
  const errors = [];

  if (!application.firstName?.trim()) errors.push('First name is required');
  if (!application.lastName?.trim()) errors.push('Last name is required');
  if (!application.position?.trim()) errors.push('Position is required');
  if (!application.resumeUrl?.trim()) errors.push('Resume URL is required');
  if (!application.resumePublicId?.trim()) errors.push('Resume Public ID is required');
  if (!application.expertise?.trim()) errors.push('Technical expertise summary is required');
  if (!application.email?.trim()) errors.push('Email is required');
  if (!application.userId?.trim()) errors.push('User ID is required');

  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (application.email && !emailRegex.test(application.email)) {
    errors.push('Valid email is required');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export default {
  APPLICATION_STATUS,
  createJobApplication,
  validateJobApplication
};